import type { SQLiteDBConnection } from "@capacitor-community/sqlite";

import { isoDate } from "@core/foundation/date";
import { uuid } from "@core/foundation/id";
import { omitUndefined } from "@core/foundation/object";
import { multiline } from "@core/foundation/string";
import { hasValue, isNullish } from "@core/foundation/types";
import type {
  JsonCreate,
  JsonPaginatedResult,
  JsonPaginationOptions,
  JsonPayload,
  JsonRepositoryHelperContract,
  JsonUpdate,
  JsonUpdateOptions,
  PK,
  SK,
} from "@core/ports/json-storage";
import {
  ETAG_FIELD,
  JsonDuplicateError,
  JsonWriteConflictError,
} from "@core/ports/json-storage";
import type { ZodObject, ZodType } from "@core/zod";
import { z } from "@core/zod";

import type { SqliteClient, SqliteOperation } from "./SqliteClient.ts";
import type { SqliteTransaction } from "./SqliteTransaction.ts";

interface Row {
  readonly PK: string;
  readonly SK: string;
  readonly SK2: null | string;
  readonly data: string;
}

const StoredEtagSchema = z.object(
  { etag: z.string().optional() },
  { etag: "The etag storage stamped on the stored record" },
);

export class SqliteRepositoryHelper<
  Identifier extends ZodObject,
  Data extends ZodObject,
  EntityType extends string,
> implements
    JsonRepositoryHelperContract<
      Identifier,
      Data,
      SqliteTransaction,
      EntityType
    >
{
  public readonly __typeTag = "JsonRepositoryHelperContract";

  public readonly entityType: EntityType;

  private readonly readSchema: ZodType<JsonPayload<Identifier, Data>>;

  private readonly sqlite: SqliteClient;

  private readonly tableName: string;

  public constructor({
    entityType,
    readSchema,
    sqlite,
    tableName,
  }: {
    readonly entityType: EntityType;
    readonly readSchema: ZodType<JsonPayload<Identifier, Data>>;
    readonly sqlite: SqliteClient;
    readonly tableName: string;
  }) {
    this.entityType = entityType;
    this.readSchema = readSchema;
    this.sqlite = sqlite;
    this.tableName = tableName;
  }

  /**
   * @throws {JsonDuplicateError} On duplicate.
   */
  public async create(
    create: JsonCreate<Identifier, Data, EntityType>,
  ): Promise<JsonPayload<Identifier, Data>> {
    const { data, operation } = this.buildInsert(create);

    const { committed } = await this.sqlite.transact([operation]);

    if (!committed) {
      throw new JsonDuplicateError(`Duplicate PK=${create.PK} SK=${create.SK}`);
    }

    return this.readSchema.parse(data);
  }

  public async getById(
    PK: PK<EntityType>,
    SK: SK,
  ): Promise<JsonPayload<Identifier, Data> | null> {
    const db = await this.sqlite.connect();
    const row = await this.selectRow(db, PK, SK);

    if (isNullish(row)) {
      return null;
    }

    return this.convertRow(row);
  }

  public async getByIds(
    keys: readonly { readonly PK: PK<EntityType>; readonly SK: SK }[],
  ): Promise<readonly JsonPayload<Identifier, Data>[]> {
    if (keys.length === 0) {
      return [];
    }

    const db = await this.sqlite.connect();

    const placeholders = keys.map(() => "(?, ?)").join(", ");
    const sql = multiline`
      SELECT
        PK, SK, SK2, data
      FROM ${this.tableName}
      WHERE (PK, SK) IN (${placeholders})
    `;

    const params = keys.flatMap(k => [k.PK, k.SK]);
    const result = await db.query(sql, params);
    const rows: readonly Row[] = result.values ?? [];

    return this.convertRows(rows);
  }

  public async getByPK(
    PK: PK<EntityType>,
    options: JsonPaginationOptions = {},
  ): Promise<JsonPaginatedResult<Identifier, Data>> {
    const sortColumn = options.index ?? "SK";

    return this.listQuery({
      options,
      params: [PK],
      sql: multiline`
        SELECT
          PK, SK, SK2, data
        FROM ${this.tableName}
        WHERE PK = ? AND ${sortColumn} IS NOT NULL
      `,
    });
  }

  public async getBySKPrefix(
    PK: PK<EntityType>,
    SKPrefix: SK,
    options: JsonPaginationOptions = {},
  ): Promise<JsonPaginatedResult<Identifier, Data>> {
    const sortColumn = options.index ?? "SK";

    return this.listQuery({
      options,
      params: [PK, `${SKPrefix}%`],
      sql: multiline`
        SELECT
          PK, SK, SK2, data
        FROM ${this.tableName}
        WHERE PK = ? AND ${sortColumn} LIKE ?
      `,
    });
  }

  public prepareCreate(
    transaction: SqliteTransaction,
    create: JsonCreate<Identifier, Data, EntityType>,
  ): void {
    const { operation } = this.buildInsert(create);
    transaction.add(operation);
  }

  public prepareUpdateById(
    transaction: SqliteTransaction,
    PK: PK<EntityType>,
    SK: SK,
    data: JsonUpdate<Data>,
    options: JsonUpdateOptions = {},
  ): void {
    transaction.add(this.buildMerge(PK, SK, data, options));
  }

  /**
   * @throws {JsonWriteConflictError} If the stored etag differs from the
   *   one the options name.
   */
  public async updateById(
    PK: PK<EntityType>,
    SK: SK,
    data: JsonUpdate<Data>,
    options: JsonUpdateOptions = {},
  ): Promise<JsonPayload<Identifier, Data> | null> {
    const { committed } = await this.sqlite.transact([
      this.buildMerge(PK, SK, data, options),
    ]);

    if (!committed) {
      return null;
    }

    return this.getById(PK, SK);
  }

  private buildInsert({
    PK,
    SK,
    SK2,
    payload,
  }: JsonCreate<Identifier, Data, EntityType>): {
    readonly data: unknown;
    readonly operation: SqliteOperation;
  } {
    if (SK2 === "") {
      throw new Error(`An empty SK2 cannot key an index: PK=${PK} SK=${SK}`);
    }

    const data = { ...payload, [ETAG_FIELD]: uuid() } as const;

    return {
      data,
      operation: async db => {
        const sql = multiline`
          INSERT INTO ${this.tableName}
            (PK, SK, SK2, data, sqlite_created_at, sqlite_updated_at)
          VALUES (?, ?, ?, ?, ?, ?)
        `;

        const now = isoDate();

        try {
          await db.run(
            sql,
            [PK, SK, SK2 ?? null, JSON.stringify(data), now, now],
            false,
          );
        } catch (error) {
          if (this.isDuplicateError(error)) {
            return { applied: false };
          }

          throw error;
        }

        return { applied: true };
      },
    };
  }

  private buildMerge(
    PK: PK<EntityType>,
    SK: SK,
    data: JsonUpdate<Data>,
    { etag }: JsonUpdateOptions,
  ): SqliteOperation {
    const update = omitUndefined(data);

    return async db => {
      const row = await this.selectRow(db, PK, SK);

      if (isNullish(row)) {
        return { applied: false };
      }

      const storedData: unknown = JSON.parse(row.data);

      const { etag: storedEtag } = StoredEtagSchema.parse(storedData);

      if (hasValue(etag) && storedEtag !== etag) {
        throw new JsonWriteConflictError(`Etag conflict PK=${PK} SK=${SK}`);
      }

      const merged = {
        ...this.readSchema.parse(storedData),
        ...update,
        [ETAG_FIELD]: uuid(),
      } as const;

      const sql = multiline`
        UPDATE ${this.tableName}
        SET data = ?, sqlite_updated_at = ?
        WHERE PK = ? AND SK = ?
      `;

      await db.run(sql, [JSON.stringify(merged), isoDate(), PK, SK], false);

      return { applied: true };
    };
  }

  private buildPaginatedResult(
    rows: readonly Row[],
    limit: number | undefined,
    cursorOf: (row: Row) => null | string,
  ): JsonPaginatedResult<Identifier, Data> {
    if (isNullish(limit)) {
      return { items: this.convertRows(rows) };
    }

    const excessRow = rows[limit];

    if (!excessRow) {
      return { items: this.convertRows(rows) };
    }

    return {
      items: this.convertRows(rows.slice(0, limit)),
      nextCursor: cursorOf(excessRow) ?? undefined,
    };
  }

  private convertRow(row: Row): JsonPayload<Identifier, Data> {
    const payload: unknown = JSON.parse(row.data);

    return this.readSchema.parse(payload);
  }

  private convertRows(
    rows: readonly Row[],
  ): readonly JsonPayload<Identifier, Data>[] {
    return rows.map(row => this.convertRow(row));
  }

  private isDuplicateError(error: unknown): boolean {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      return (
        message.includes("duplicate") ||
        message.includes("primary key") ||
        message.includes("unique constraint")
      );
    }

    return false;
  }

  private async listQuery({
    options: { cursor, index, limit, order = "asc" },
    params,
    sql,
  }: {
    readonly options: JsonPaginationOptions;
    readonly params: readonly string[];
    readonly sql: string;
  }): Promise<JsonPaginatedResult<Identifier, Data>> {
    const sortColumn = index ?? "SK";

    const cursorCondition = { asc: ">", desc: "<" } as const;

    const orderDirection = { asc: "ASC", desc: "DESC" } as const;

    let fullSql = sql;

    let fullParams = params;

    if (hasValue(cursor)) {
      fullSql += ` AND ${sortColumn} ${cursorCondition[order]} ?`;
      fullParams = [...fullParams, cursor];
    }

    fullSql += ` ORDER BY ${sortColumn} ${orderDirection[order]}`;

    if (hasValue(limit)) {
      fullSql += ` LIMIT ${limit + 1}`;
    }

    const db = await this.sqlite.connect();
    const result = await db.query(fullSql, [...fullParams]);
    const rows: readonly Row[] = result.values ?? [];

    return this.buildPaginatedResult(rows, limit, row => row[sortColumn]);
  }

  private async selectRow(
    db: SQLiteDBConnection,
    PK: PK<EntityType>,
    SK: SK,
  ): Promise<Row | undefined> {
    const sql = multiline`
      SELECT
        PK, SK, SK2, data
      FROM ${this.tableName}
      WHERE PK = ? AND SK = ? LIMIT 1
    `;

    const result = await db.query(sql, [PK, SK]);
    const rows: readonly Row[] = result.values ?? [];

    return rows[0];
  }
}
